import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, setUnauthorizedCallback } from '../../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Check localStorage (remember me session)
        const token = localStorage.getItem('adminToken');
        const savedUser = localStorage.getItem('adminUser');
        const expiry = localStorage.getItem('adminTokenExpiry');

        if (token && savedUser && expiry && Date.now() < parseInt(expiry, 10)) {
            try {
                setUser(JSON.parse(savedUser));
                setLoading(false);
                return;
            } catch (e) { /* fall through */ }
        }

        // Clear expired / invalid localStorage session
        if (token || savedUser) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            localStorage.removeItem('adminTokenExpiry');
        }

        // Check sessionStorage (non-remember-me session)
        const sessionToken = sessionStorage.getItem('adminToken');
        const sessionUser = sessionStorage.getItem('adminUser');

        if (sessionToken && sessionUser) {
            try {
                setUser(JSON.parse(sessionUser));
            } catch (e) {
                sessionStorage.removeItem('adminToken');
                sessionStorage.removeItem('adminUser');
            }
        }

        setLoading(false);
    }, []);

    const login = async (email, password, captchaToken, rememberMe = false) => {
        try {
            // Verify CAPTCHA token on backend
            const response = await authApi.login(email, password, captchaToken);
            if (response.token && response.user) {
                // Check if user has admin role (Role: 0 = Admin, 1 = Manager, 2 = Staff)
                if (response.user.roleId !== 0 && response.user.roleId !== 1) {
                    throw new Error('Bu hesap ile admin paneline erişim yetkiniz bulunmamaktadır.');
                }

                if (rememberMe) {
                    const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
                    localStorage.setItem('adminToken', response.token);
                    localStorage.setItem('adminUser', JSON.stringify(response.user));
                    localStorage.setItem('adminTokenExpiry', expiry.toString());
                    // Clear any leftover session storage
                    sessionStorage.removeItem('adminToken');
                    sessionStorage.removeItem('adminUser');
                } else {
                    // Clear any previous remember-me data
                    localStorage.removeItem('adminToken');
                    localStorage.removeItem('adminUser');
                    localStorage.removeItem('adminTokenExpiry');
                    sessionStorage.setItem('adminToken', response.token);
                    sessionStorage.setItem('adminUser', JSON.stringify(response.user));
                }

                setUser(response.user);
                return { success: true };
            }

            throw new Error('Giriş başarısız');
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    const logout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminTokenExpiry');
        sessionStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminUser');
        setUser(null);
    };

    // Register callback with API service for handling 401 responses
    useEffect(() => {
        const handleUnauthorized = () => {
            logout();
            navigate('/admin/login', { replace: true });
        };
        setUnauthorizedCallback(handleUnauthorized);
    }, [navigate]);

    const value = {
        user,
        login,
        logout,
        loading,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
